/**
 * FILE: packages/supabase/src/dbClient.ts
 * PURPOSE: Injectable DbClient port and adapter for service-layer database access.
 * AGENT_CONTEXT: Services should accept a DbClient parameter defaulting to defaultDbClient instead of importing supabase directly.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from './client'
import type { Database } from './types'

/**
 * Narrow port exposing only the Supabase capabilities services use:
 * table queries (`from`) and database functions (`rpc`).
 */
export type DbClient = Pick<SupabaseClient<Database>, 'from' | 'rpc'>

/**
 * Behaviorless adapter that wraps any SupabaseClient as a DbClient.
 */
export const createDbClient = (client: SupabaseClient<Database>): DbClient => ({
  from: client.from.bind(client),
  rpc: client.rpc.bind(client),
})

/**
 * Production default backed by the singleton Supabase client.
 *
 * Lazily creates the underlying client on the first method call so that
 * importing this module does not require VITE_SUPABASE_URL/ANON_KEY in
 * test/CI environments that never use the default client.
 */
/**
 * Production default backed by the singleton Supabase client.
 *
 * The underlying `supabase` singleton is lazy, so importing this module does not
 * require VITE_SUPABASE_URL/ANON_KEY until a query is actually executed.
 */
export const defaultDbClient: DbClient = createDbClient(supabase)
